import { cn } from "@/lib/cn";
import type { ComplaintBusinessStage, ComplaintSeverity, ComplaintSla, ComplaintState } from "@/api/types";
import {
  SEVERITY_LABEL,
  SEVERITY_TONE,
  SLA_TONE,
  stateLabel,
  stateTone,
  type DsTone,
} from "./constants";

export const TONE_CLASS: Record<DsTone, string> = {
  primary: "bg-primary/10 text-primary border-primary/20",
  amber: "bg-warning text-warning-foreground border-warning-foreground/20",
  destructive: "bg-destructive/10 text-destructive border-destructive/30",
  success: "bg-success text-success-foreground border-success-foreground/20",
  muted: "bg-muted text-muted-foreground border-border",
  neutral: "bg-muted text-foreground border-border",
};

function Pill({ tone, children, className }: { tone: DsTone; children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function ComplaintStatusBadge({ state }: { state: ComplaintState }) {
  return <Pill tone={stateTone(state)}>{stateLabel(state)}</Pill>;
}

// COMP-LIFECYCLE — Level-2 human business stage (backend-owned label), toned by state.
// Replaces the raw technical state badge in the list/card (AC4: 0 technical labels).
export function ComplaintStageBadge({
  stage,
  state,
}: {
  stage: ComplaintBusinessStage;
  state: ComplaintState;
}) {
  return <Pill tone={stateTone(state)}>{stage.label}</Pill>;
}

export function SeverityBadge({ severity }: { severity: ComplaintSeverity }) {
  return (
    <Pill tone={SEVERITY_TONE[severity]}>
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          severity === "critical" && "bg-destructive",
          severity === "high" && "bg-warning-foreground",
          severity === "medium" && "bg-foreground/50",
          severity === "low" && "bg-muted-foreground",
        )}
        aria-hidden
      />
      {SEVERITY_LABEL[severity]}
    </Pill>
  );
}

export function SlaBadge({ sla }: { sla: ComplaintSla }) {
  if (sla.status === "none") return null;
  return <Pill tone={SLA_TONE[sla.status]}>{sla.label}</Pill>;
}
